import { useState, type FormEvent, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthError } from '@yuan-math/auth';
import { Button } from '@yuan-math/ui';
import { PasswordField } from '../components/PasswordField.tsx';
import { useAuth } from '../state/AuthContext.tsx';
import { AccountsUnavailable } from './RegisterPage.tsx';

export function SignInPage(): JSX.Element {
  const { signIn, configured, signedIn, ready } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<AuthError | null>(null);
  const [busy, setBusy] = useState(false);

  if (!configured) return <AccountsUnavailable />;

  // See `RegisterPage`: a form asking somebody to sign in again is the wrong
  // answer to "we have not finished asking who you are".
  if (!ready) return <div className="route-loading" aria-busy="true" />;

  if (signedIn) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">You are signed in</h1>
        <div className="button-row">
          <Link to="/home" className="btn btn--primary">Go to my home</Link>
        </div>
      </div>
    );
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
      navigate('/home');
    } catch (cause) {
      setError(cause instanceof AuthError ? cause : new AuthError('not-supported', 'Could not sign in just now.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page--narrow">
      <h1 className="page__title">Sign in</h1>
      <p className="page__lead">Pick up where you left off, on any device.</p>

      <form className="start-form" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="field__input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
          />
        </label>

        <PasswordField
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        >
          <span className="field__hint">
            <Link to="/forgot">Forgotten your password?</Link>
          </span>
        </PasswordField>

        {error ? (
          <p className="form-error" role="alert">
            {error.message}
            {error.code === 'not-activated' ? (
              <>
                {' '}
                <Link to={`/activate?sent=${encodeURIComponent(email.trim())}`}>Nothing arrived?</Link>
              </>
            ) : null}
          </p>
        ) : null}

        <Button type="submit" size="lg" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>

      </form>

      {/* The second person this page gets. Somebody with no account cannot use
          anything above, and a line of hint text under the submit button is the
          size reserved for things that do not matter. */}
      <section className="signin-alt">
        <p className="signin-alt__question">No account yet?</p>
        <p className="signin-alt__note">
          An account keeps your progress across devices — or start straight away without one; nothing here is
          locked behind it.
        </p>
        <div className="button-row">
          <Link to="/register" className="btn btn--secondary btn--lg">
            Create an account
          </Link>
          <Link to="/start" className="btn btn--ghost btn--lg">
            Keep learning without one
          </Link>
        </div>
      </section>
    </div>
  );
}
